import axios from 'axios';

// API Base URL - should be set in environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-api-domain.com';

// Optional: Socket API base URL override if needed
export const SOCKET_API_BASE_URL = process.env.REACT_APP_SOCKET_API_URL || API_BASE_URL;

// Auth credentials - should be set in environment variables
const AUTH_KEY = process.env.REACT_APP_AUTH_KEY || 'your_auth_key';
const AUTH_VALUE = process.env.REACT_APP_AUTH_VALUE || 'your_auth_value';

// Debug logging
if (process.env.NODE_ENV === 'development') {
    console.log('API Configuration:', {
        API_BASE_URL,
        SOCKET_API_BASE_URL,
        AUTH_KEY: AUTH_KEY ? '***SET***' : '***NOT SET***',
        AUTH_VALUE: AUTH_VALUE ? '***SET***' : '***NOT SET***'
    });
}

// Debug mode
const DEBUG_MODE = process.env.REACT_APP_DEBUG === 'true';

// API Endpoints (allow env overrides to match backend without code changes)
export const API_ENDPOINTS = {
    SIGN_IN: process.env.REACT_APP_SIGN_IN_ENDPOINT || 'api/v1/auth/login',
    SIGN_UP: process.env.REACT_APP_SIGN_UP_ENDPOINT || 'api/v1/auth/register',
    VERIFY_EMAIL_OTP: process.env.REACT_APP_VERIFY_EMAIL_OTP_ENDPOINT || 'api/v1/verification/verify-email',
    ENTER_MOBILE: process.env.REACT_APP_ENTER_MOBILE_ENDPOINT || 'api/v1/verification/send-phone-verification',
    VERIFY_MOBILE_OTP: process.env.REACT_APP_VERIFY_MOBILE_OTP_ENDPOINT || 'api/v1/verification/verify-phone',
    VERIFY_FORGOT_OTP: process.env.REACT_APP_VERIFY_FORGOT_OTP_ENDPOINT || 'api/v1/auth/password/verify-otp',
    FORGOT_PASSWORD: process.env.REACT_APP_FORGOT_PASSWORD_ENDPOINT || 'api/v1/auth/forgot-password',
    VALIDATE_FORGOT_PASSWORD: process.env.REACT_APP_VALIDATE_FORGOT_PASSWORD_ENDPOINT || 'api/v1/auth/verify-otp',
    REFRESH_TOKEN: process.env.REACT_APP_REFRESH_TOKEN_ENDPOINT || 'api/v1/auth/refresh-token',
    LOGOUT: process.env.REACT_APP_LOGOUT_ENDPOINT || 'api/v1/auth/logout',
    CHECK_USER_NAME: process.env.REACT_APP_CHECK_USER_NAME_ENDPOINT || 'api/v1/auth/check-username',
    CHECK_EMAIL: process.env.REACT_APP_CHECK_EMAIL_ENDPOINT || 'api/v1/auth/check-email',
    SEND_EMAIL_VERIFICATION: process.env.REACT_APP_SEND_EMAIL_VERIFICATION_ENDPOINT || 'api/v1/verification/send-email-verification',
    CREATE_PROFILE: process.env.REACT_APP_CREATE_PROFILE_ENDPOINT || 'api/v1/user',
    UPLOAD_FILE: process.env.REACT_APP_UPLOAD_FILE_ENDPOINT || 'api/v1/upload',
    CATEGORY_GUEST_LIST: process.env.REACT_APP_CATEGORY_GUEST_LIST_ENDPOINT || 'api/v1/category/guest-list',

    STRIPE_STATUS: process.env.REACT_APP_STRIPE_STATUS_ENDPOINT || 'api/v1/stripe/status',
    STRIPE_ONBOARDING_LINK: process.env.REACT_APP_STRIPE_ONBOARDING_LINK_ENDPOINT || 'api/v1/stripe/onboarding-link',
    STRIPE_LOGIN_LINK: process.env.REACT_APP_STRIPE_LOGIN_LINK_ENDPOINT || 'api/v1/stripe/login-link',

    // Wallet/balance within Pagent and transfers to Stripe
    WALLET_BALANCE: process.env.REACT_APP_WALLET_BALANCE_ENDPOINT || 'api/v1/user/balance',
    WALLET_TRANSACTIONS: process.env.REACT_APP_WALLET_TRANSACTIONS_ENDPOINT || 'api/v1/user/transactions',
    WALLET_TRANSFER_TO_STRIPE: process.env.REACT_APP_WALLET_TRANSFER_TO_STRIPE_ENDPOINT || 'api/v1/wallet/transfer-to-stripe',
    WALLET_CASHOUT: process.env.REACT_APP_WALLET_CASHOUT_ENDPOINT || 'api/v1/user/cashout'
};

// Token management helpers
export const storeToken = (accessToken) => {
    localStorage.setItem('accessToken', accessToken);
};

export const storeRefreshToken = (refreshToken) => {
    localStorage.setItem('refreshToken', refreshToken);
};

export const getToken = () => {
    return localStorage.getItem('accessToken');
};

export const getRefreshToken = () => {
    return localStorage.getItem('refreshToken');
};

export const removeToken = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
};

// Language helper (you can implement your own language detection)
const getLanguage = () => {
    return navigator.language || 'en-US';
};

// Encode credentials for Basic Auth
const encodeCredentials = (username, password) => {
    if (!username || !password) {
        throw new Error('Auth credentials are missing');
    }
    return btoa(`${username}:${password}`);
};

// Token expiration check
export const isTokenExpired = (token) => {
    if (!token) {
        return false;
    }
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid token');
        }

        const decodedPayload = JSON.parse(atob(parts[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        return decodedPayload.exp < currentTime;
    } catch (error) {
        return true;
    }
};

// Check if environment is properly configured
export const isEnvironmentConfigured = () => {
    return API_BASE_URL !== 'https://your-api-domain.com' &&
        AUTH_KEY !== 'your_auth_key' &&
        AUTH_VALUE !== 'your_auth_value';
};

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
});

// Request interceptor for authentication headers
api.interceptors.request.use(async (config) => {
    const isGuest = !getToken(); // Simplified guest check

    // An explicitly-provided accesstoken header (the signup/onboarding token
    // that authenticates the verification routes before a real session exists)
    // must survive the base-header overwrite below.
    const explicitAccessToken = config.headers?.accesstoken;

    // Cache-buster for GETs. Opt out with `cacheBust: false` on the request
    // config — some endpoints validate query params strictly (Joi rejects
    // unknown keys), so an extra `_ts` would 400 them. The no-cache headers
    // below still prevent stale responses.
    const method = (config.method || 'get').toLowerCase();
    if (method === 'get' && config.cacheBust !== false) {
        config.params = { ...(config.params || {}), _ts: Date.now() };
    }

    // Don't override headers on multipart uploads — let axios set the boundary itself.
    const isMultipart = config.data instanceof FormData;

    const baseHeaders = isMultipart
        ? {
              Accept: 'application/json, text/plain, */*',
              'Accept-Language': getLanguage(),
              Authorization: `Basic ${encodeCredentials(AUTH_KEY, AUTH_VALUE)}`,
          }
        : {
              'Content-Type': 'application/json',
              Accept: 'application/json, text/plain, */*',
              'Accept-Language': getLanguage(),
              Authorization: `Basic ${encodeCredentials(AUTH_KEY, AUTH_VALUE)}`,
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
          };

    config.headers = baseHeaders;

    if (explicitAccessToken) {
        config.headers.accesstoken = explicitAccessToken;
    } else if (!isGuest) {
        const accessToken = getToken();
        if (accessToken) {
            // Backend expects the user's JWT in a custom lowercase `accesstoken`
            // header — NOT `Authorization: Bearer ...` (Authorization is already
            // taken by the app-wide Basic-Auth gate).
            config.headers.accesstoken = `Bearer ${accessToken}`;
        }
    }

    // Debug logging
    if (DEBUG_MODE) {
        const fullUrl = config.baseURL + '/' + config.url;
        console.log('API Request:', {
            fullUrl,
            url: config.url,
            method: config.method,
            headers: config.headers,
            data: config.data
        });
    }

    return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
    (response) => {
        // Debug logging
        if (DEBUG_MODE) {
            console.log('API Response:', {
                url: response.config.url,
                status: response.status,
                data: response.data
            });
        }
        return response;
    },
    async (error) => {
        // Debug logging
        if (DEBUG_MODE) {
            console.log('API Error:', {
                url: error.config?.url,
                status: error.response?.status,
                data: error.response?.data
            });
        }

        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = getRefreshToken();
                if (!refreshToken) {
                    throw new Error('No refresh token available');
                }

                const response = await api.post(API_ENDPOINTS.REFRESH_TOKEN, {
                    refreshToken: refreshToken
                });

                const newToken = response?.data?.data?.token;
                const newRefreshToken = response?.data?.data?.refreshToken;
                if (newToken) {
                    storeToken(newToken);
                }
                if (newRefreshToken) {
                    // Backend rotates refresh tokens — old one is invalidated, so
                    // we MUST persist the new one.
                    storeRefreshToken(newRefreshToken);
                }

                originalRequest.headers.accesstoken = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                removeToken();
                localStorage.removeItem('pagent_user');
                window.location.href = '/';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default api; 